/* eslint-disable no-case-declarations */
import {
  Component,
  ComponentContainer,
  ComponentInstance,
  Props,
  Effect,
  ValueUpdater,
  createSignal,
  Diff,
  State,
  isObjectLike,
  isArray,
  Callback,
  appendUniqueArray,
  deleteArrayItem,
  getNestedValue,
  setNestedValue,
  CommonFn
} from "@zyzgroup/core-common";
import { h, renderVNode } from "./vnode";
import { isInputCheckbox, isInputRadio, isSelect } from "../dom";

// 为什么真正 cycle detection 很难？
// 因为 JS effect 是动态的：
// effect(() => {
//   if (flag()) {
//     a();
//   } else {
//     b();
//   }
// });
// 依赖会变化：
// effect -> a
// effect -> b
// 所以：
// Reactive graph != 静态 graph

// DFS
// function hasCycle(
//   node: ReactiveNode,
//   visiting = new Set<ReactiveNode>(),
//   visited = new Set<ReactiveNode>()
// ): boolean {
//   if (visited.has(node)) {
//     return false;
//   }
//   if (visiting.has(node)) {
//     return true;
//   }
//   visiting.add(node);
//   for (const dep of node.deps) {
//     if (hasCycle(dep, visiting, visited)) {
//       return true;
//     }
//   }
//   visiting.delete(node);
//   visited.add(node);
//   return false;
// }

export function createMiniReact() {
  // 存储每个组件实例的数据
  const componentInstances = new Map<ComponentContainer, ComponentInstance>();
  let currentComponentInstance: ComponentInstance | null = null;

  // 批量渲染队列
  const pendingRenders = new Set<ComponentInstance>();
  let isRendering = false;

  // ---------------- useState ----------------
  function useState<T = any>(initialValue: T): [T, ValueUpdater<T>] {
    const instance = currentComponentInstance as ComponentInstance;
    const idx = instance.stateIndex;
    if (!(idx in instance.states)) {
      instance.states[idx] = initialValue;
    }
    function setState(newValue: T | ((prev: T) => T)): void {
      let resolvedValue;
      if (typeof newValue == "function") {
        resolvedValue = (newValue as (v: T) => T)(instance.states[idx]);
      } else {
        resolvedValue = newValue;
      }
      // 实际上：React setState 会把一个 update 入队到 hook 的更新队列，保留旧的 memoized state
      // 真正把这些 update 应用到 state 并得到 “本次渲染看到的 state” 是在下一次 render 过程中完成的
      // 每个 update 闭包里引用的 state 仍是旧 render 中 state 的值

      // “Rendering” means that React is calling your component, which is a function.
      // The JSX you return from that function is like a snapshot of the UI in time.
      // Its props, event handlers, and local variables were all calculated using its state at the time of the render.
      // A state variable’s value never changes within a render, even if its event handler’s code is asynchronous.
      // Setting state only changes it for the next render.

      // But what if you wanted to read the latest state before a re-render? You’ll want to use a state updater function,
      // It is an uncommon use case, but if you would like to update the same state variable multiple times before the next render,
      // instead of passing the next state value like setNumber(number + 1),
      // you can pass a function that calculates the next state based on the previous one in the queue, like setNumber(n => n + 1).
      // It is a way to tell React to “do something with the state value” instead of just replacing it.
      // During the re-render, React will process the queue. Updater functions run during rendering, so updater functions must be pure and only return the result.
      // Don’t try to set state from inside of them or run other side effects.

      // React waits until all code in the event handlers has run before processing your state updates.
      // This lets you update multiple state variables, even from multiple components, without triggering too many re-renders.
      // But this also means that the UI won’t be updated until after your event handler, and any code in it, completes.
      // This behavior, also known as **batching**, makes your React app run much faster.
      // It also avoids dealing with confusing “half-finished” renders where only some of the variables have been updated.
      // React keeps the state values “fixed” within one render’s event handlers.
      // but React does not batch across multiple intentional events like clicks, each click is handled separately.
      // This ensures that, for example, if the first button click disables a form, the second click would not submit it again.

      if (instance.states[idx] !== resolvedValue) {
        instance.states[idx] = resolvedValue;
        scheduleRender(instance); // setState 会导致 重新渲染
      }
    }
    instance.stateIndex++;
    return [instance.states[idx], setState as ValueUpdater<T>];
  }

  // ---------------- useRef ----------------
  function useRef<T = any>(initialValue: T) {
    const [ref, unused] = useState({ current: initialValue });
    return ref;
  }

  // ---------------- useEffect ----------------
  // Setting state immediately in an Effect is like :
  // The Effect runs, it sets the state, which causes a re-render,
  // which causes the Effect to run, it sets the state again, this causes another re-render, and so on.
  // Effects should usually synchronize your components with an external system.
  // If there’s no external system and you only want to adjust some state based on other state, you might not need an Effect.

  // Effects don’t run on the server

  function useEffect(effect: Effect, deps?: any[]): void {
    const instance = currentComponentInstance as ComponentInstance;
    const idx = instance.effectIndex;
    if (!instance.pendingEffects) {
      instance.pendingEffects = [];
    }
    instance.pendingEffects.push({ effect, deps, idx });
    instance.effectIndex++;
  }

  function flushEffects(instance: ComponentInstance) {
    if (!instance.pendingEffects) {
      return;
    }
    instance.pendingEffects.forEach(({ effect, deps, idx }) => {
      const prevEffect = instance.effects[idx];
      const hasChanged =
        !deps ||
        !prevEffect ||
        !prevEffect.deps ||
        deps.length !== prevEffect.deps.length ||
        deps.some((d, i) => d !== prevEffect.deps![i]);
      if (hasChanged) {
        prevEffect.cleanup?.();
        const cleanup = effect();
        if (cleanup instanceof Promise) {
          throw new Error("useEffect callback must not return Promise");
        }
        instance.effects[idx] = { deps: deps ? [...deps] : undefined, cleanup };
      }
    });
    instance.pendingEffects = [];
  }

  function useEffectEvent(fn: (...args: any[]) => void) {
    const ref = useRef(fn);
    ref.current = fn;
    return useCallback((...args) => ref.current(...args), []);
  }

  // ---------------- useCallback ----------------
  function useCallback(fn: CommonFn, deps?: any[]): CommonFn {
    const instance = currentComponentInstance as ComponentInstance;
    const idx = instance.callbackIndex;
    const cache = instance.callbacks[idx] || { fn: fn, deps: undefined };
    const hasChanged =
      !deps ||
      !cache.deps ||
      deps.length !== cache.deps.length ||
      deps.some((dep, i) => dep !== cache.deps![i]);
    if (hasChanged) {
      cache.fn = fn;
      cache.deps = deps ? [...deps] : undefined;
    }
    instance.callbacks[idx] = cache;
    instance.callbackIndex++;
    return cache.fn;
  }

  // ---------------- scheduleRender ----------------
  function scheduleRender(instance: ComponentInstance): void {
    pendingRenders.add(instance);
    if (!isRendering) {
      isRendering = true;
      setTimeout(flushRenders, 0); // 批量渲染
    }
  }

  function flushRenders() {
    while (pendingRenders.size) {
      const renders = [...pendingRenders];
      pendingRenders.clear();
      renders.forEach((instance) => {
        render(instance.component, instance.container, instance.props);
      });
    }
    isRendering = false;
  }

  // ---------------- render ----------------
  function render(
    component: Component,
    container: ComponentContainer,
    props: Props = {}
  ): ComponentInstance {
    let instance = componentInstances.get(container);
    if (!instance || instance.component !== component) {
      instance = {
        component,
        props,
        states: [],
        stateIndex: 0,
        effects: [],
        effectIndex: 0,
        pendingEffects: [],
        callbacks: [],
        callbackIndex: 0,
        children: [],
        mounted: false,
        container,
        vnode: undefined
      };
      componentInstances.set(container, instance);
    } else {
      instance.props = props;
    }

    currentComponentInstance = instance;

    // 每次调用 组件函数 前，重置索引
    instance.stateIndex = 0;
    instance.effectIndex = 0;
    instance.callbackIndex = 0;

    // 渲染组件
    // since components don’t have side effects in render,
    // React can pause rendering components that aren’t as important to update,
    // and only come back to them later when it’s needed.
    // this means that rendering logic can be run multiple times in a way that allows React to give your user a pleasant user experience
    const newVNode = component(props); // 调用 组件函数，返回 vnode
    renderVNode(container, newVNode, instance.vnode);
    instance.vnode = newVNode;

    instance.mounted = true;

    currentComponentInstance = null;

    // flush effects after render
    // Side effects should not run in render
    flushEffects(instance);

    return instance;
  }

  // ---------------- unmount ----------------
  function unmount(instance: ComponentInstance) {
    instance.effects.forEach((e) => e.cleanup?.());
    if (instance.children) {
      instance.children.forEach(unmount);
    }
    if (instance.container && "innerHTML" in instance.container) {
      instance.container.innerHTML = "";
    }
    componentInstances.delete(instance.container);
  }

  return {
    useState,
    useRef,
    useEffect,
    useEffectEvent,
    useCallback,
    render,
    unmount,
    scheduleRender
  };
}

// const { useState, useEffect, render, unmount } = createMiniReact();

// function Timer(props?: Props) {
//   const [time, setTime] = useState<number>(props?.start || 0);
//   useEffect(() => {
//     const id = setInterval(() => setTime((t) => t + 1), 1000);
//     return () => clearInterval(id);
//   }, []);
//   return h("div", {}, `Timer: ${time}s`);
// }

// function Counter(props?: Props) {
//   const [count, setCount] = useState<number>(props?.initial || 0);
//   const [showTimer, setShowTimer] = useState(true);
//   return h(
//     "div",
//     { style: "margin:10px; border:1px solid #ccc; padding:10px;" },
//     [
//       h("h2", {}, `Count: ${count}`),
//       h("button", { onclick: () => setCount((c) => c + 1) }, "Increment"),
//       h(
//         "button",
//         { onclick: () => setShowTimer((s) => !s) },
//         showTimer ? "Hide Timer" : "Show Timer"
//       ),
//       showTimer && h(Timer, { start: 0 }) // 嵌套 Timer 组件
//     ]
//   );
// }

// function App() {
//   return h("div", {}, [
//     h("h1", {}, "Mini React Demo"),
//     h(Counter, { initial: 5 })
//   ]);
// }

// // 渲染到页面
// const root = document.getElementById("root"); // <div id="root"></div>
// if (root) {
//   const appInstance = render(App, root);
//   // // 10 秒后卸载组件
//   // setTimeout(() => {
//   //   console.log("Unmounting Counter...");
//   //   // unmount(Counter);
//   //   unmount(appInstance.children[1]);
//   // }, 10000);
// }

export function createMiniVue() {
  // 响应式系统 的核心就是 数据代理
  // 数据 在 getter 时进行 依赖收集，在 setter 时对依赖 触发更新

  // 测试用例：reactive.browser.test.js

  let activeEffect: Effect | null;

  // 订阅者 : 执行一次，自动收集依赖
  function watchEffect(effect: Effect) {
    activeEffect = effect;
    // effect 里面使用 reactiveVue3代理的数据
    // get时自动收集依赖，set时自动通知依赖更新
    effect();
    activeEffect = null;
  }

  // 订阅器 : 发布订阅
  class Dep {
    subscribers: Set<Effect> = new Set();
    add() {
      if (activeEffect && !this.subscribers.has(activeEffect)) {
        this.subscribers.add(activeEffect);
      }
    }
    notify() {
      this.subscribers.forEach((effect) => effect());
    }
  }

  const targetKeyDepMap = new WeakMap<State | any[], Map<PropertyKey, Dep>>();
  // const targetKeyDepMap = new Map<State | any[], Map<PropertyKey, Dep>>();

  function getDep(target: State | any[], key: PropertyKey) {
    let keyDepMap = targetKeyDepMap.get(target);
    if (!keyDepMap) {
      keyDepMap = new Map();
      targetKeyDepMap.set(target, keyDepMap);
    }
    let dep = keyDepMap.get(key);
    if (!dep) {
      dep = new Dep();
      keyDepMap.set(key, dep);
    }
    return dep;
  }

  function reactiveArray(arr: any[], dep: Dep, deepReactive = reactiveVue2) {
    if (!Array.isArray(arr)) {
      return;
    }
    const originProto = Array.prototype;
    const arrayProto = Object.create(originProto);
    // 数组的变异方法
    ["push", "pop", "shift", "unshift", "splice", "sort", "reverse"].forEach(
      (method) => {
        arrayProto[method] = function (...args: any[]) {
          // const oldValue = this.slice();
          // 对新添加的对象递归响应式处理
          if (["push", "unshift", "splice"].includes(method)) {
            (method === "splice" ? args.slice(2) : args).forEach(
              (item, index) => {
                if (isObjectLike(item)) {
                  args[index] = deepReactive(item);
                }
                dep.add();
              }
            );
          }
          (originProto as any)[method].apply(this, args);
          const newValue = this.slice();
          return newValue;
        };
      }
    );
    // 这里每次赋值都会重新设置原型和劫持方法，如果外部频繁替换数组对象，可能产生性能问题（Vue2 也是这样处理的，但需要注意）
    Object.setPrototypeOf(arr, arrayProto);

    // 不可行： JavaScript 中原生限制：数组的 length 属性是不可配置的（configurable: false），不像普通对象的属性那样可以随意重新定义
    // // 劫持 length 属性
    // let currentLength = value.length;
    // Object.defineProperty(value, "length", {
    //   configurable: true,
    //   enumerable: false,
    //   get() {
    //     return currentLength;
    //   },
    //   set(newLength) {
    //     if (newLength !== currentLength) {
    //       currentLength = newLength;
    //       callback(); // 触发回调
    //     }
    //   }
    // });

    // 劫持已有索引
    // 依赖收集 (dep) 会自动去重
    arr.forEach((item: any, index: number) => {
      if (isObjectLike(item)) {
        arr[index] = deepReactive(item);
      }
      dep.add();
    });
  }

  /**
   * 监听器 : defineProperty 数据原型劫持
   *
   * 无法劫持新创建的属性
   *
   * 无法劫持数组 索引赋值 和 length修改，例如
   * arr[0] = 123; // 不会触发 dep.notify()
   * arr.length = 0; // 不会触发 dep.notify()
   *
   * @param raw
   * @param deep
   * @returns
   */
  function reactiveVue2<T extends State = State>(raw: T): T {
    (Object.keys(raw) as Array<keyof T>).forEach((key) => {
      const dep = getDep(raw, key);

      let value = raw[key];

      Object.defineProperty(raw, key, {
        configurable: true, // 是否可删除、可配置
        enumerable: true, // 是否可枚举

        // 数据描述符
        // value : 默认为 undefined
        // writable : 是否可读写

        // 存取描述符
        get() {
          if (isObjectLike(value)) {
            if (Array.isArray(value)) {
              // array
              reactiveArray(value, dep, reactiveVue2);
            } else {
              // object
              reactiveVue2(value);
            }
          }
          // 通过 getter 自动收集依赖
          dep.add();
          return value; // 避免死循环
        },

        set(newValue) {
          if (value !== newValue) {
            // const oldValue = value;
            value = newValue;
            dep.notify();
          }
        }
      });
    });

    return raw;
  }

  function ref<T = any>(value: T) {
    return reactiveVue2({ value });
  }

  const reactive = reactiveVue3;

  /**
   * 监听器 : Proxy 数据代理
   *
   * 可以监听新创建的属性
   *
   * 可以监听数组的变化
   *
   * 可以监听利用索引修改数组元素
   *
   * @param raw
   * @param deep
   * @returns
   */
  function reactiveVue3<T extends State = State>(raw: T): T {
    if (!isObjectLike(raw)) {
      return raw;
    }

    const handler: ProxyHandler<T> = {
      // 拦截对象属性的读取
      // target 目标对象、key 键值、receiver 当前proxy实例
      get(target, key, receiver) {
        // console.log("get", key);

        // 只要 Proxy 对象具有的代理方法，Reflect 对象全部具有，以静态方法的形式存在
        // - 修改某些 Object 方法的返回结果，让其变得更合理（定义不存在属性行为的时候不报错而是返回 false）
        let result = Reflect.get(target, key, receiver);
        // let result = target[key];

        const dep = getDep(target, key);

        // 递归
        if (isObjectLike(result) && !("isYZProxy" in result)) {
          // array or object

          // if (Array.isArray(result)) {
          //   reactiveArray(result, dep, reactiveVue3, {
          //     callback: (diff) => {
          //       dep.notify(diff);
          //       if (callback) {
          //         callback(diff);
          //       }
          //     },
          //     path: fullpath
          //   });
          //   return result;
          // }

          const deepProxy = reactiveVue3(result) as any;

          if (Array.isArray(result)) {
            deepProxy["YZProxyArrayDep"] = dep;
            // (deepProxy as any[]).forEach((r, i) => {
            //   const depArray = getDep(r, i);
            //   depArray.add(); // 自动收集依赖
            //   // dep.add(); // 自动收集依赖
            // });
          }

          // Reflect.set(target, key, deepProxy);
          result = deepProxy;
        }

        dep.add(); // 自动收集依赖

        return result;
      },

      // 拦截对象属性的设置
      // target 目标对象、key 键值、value 值、receiver 当前proxy实例
      set(target, key, value, receiver) {
        // console.log("set", key, value);

        const oldValue = Reflect.get(target, key, receiver);
        const result = Reflect.set(target, key, value, receiver);

        if (key == "YZProxyArrayDep") {
          return result;
        }

        if (oldValue !== value) {
          // arr.push() 会触发2次 set
          // 一次是 set arr.数字索引 key=index_number
          // 一次是 set arr.length  key=length
          // 通过 oldValue !== value 排除 第2次触发 set arr.length
          let dep;
          if (Array.isArray(target)) {
            dep = target["YZProxyArrayDep"];
            // dep = getDep(target, key);
          } else {
            dep = getDep(target, key);
          }

          dep.notify(); // 通知依赖更新
        }

        return result;
      },

      // 拦截 `delete proxy[key]` 的操作，返回一个布尔值
      deleteProperty(target, key) {
        // console.log("delete", target, key);

        const hasKey = key in target;
        if (!hasKey) {
          return false;
        }

        let oldValue = typeof key === "symbol" ? undefined : target[key];
        if (isObjectLike(oldValue)) {
          oldValue = Array.isArray(oldValue) ? [...oldValue] : { ...oldValue };
        }

        const result = Reflect.deleteProperty(target, key);
        // delete target[key];

        if (result) {
          let dep;
          if (Array.isArray(target)) {
            dep = target["YZProxyArrayDep"];
            // dep = getDep(target, key);
          } else {
            dep = getDep(target, key);
          }

          dep.notify();
        }

        return result;
      },

      // 拦截 `key in proxy` 的操作，返回一个布尔值
      has(target, key) {
        // console.log("has", key);
        return key === "isYZProxy" || key in target;
      }

      // 拦截 `Object.keys(proxy)`、`for...in` 等循环，返回一个数组
      // ownKeys(target) {
      //   return [];
      // },

      // 拦截 `Object.getOwnPropertyDescriptor(proxy, key)`，返回属性的描述对象
      // getOwnPropertyDescriptor(target, key) {
      //   return {};
      // },

      // 拦截 `Object.defineProperty(proxy, key, desc`，返回一个布尔值
      // defineProperty(target, key, desc) {
      //   return true;
      // },

      // 拦截 `Object.preventExtensions(proxy)`，返回一个布尔值
      // preventExtensions(target) {
      //   return true;
      // },

      // 拦截 `Object.getPrototypeOf(proxy)`，返回一个对象
      // getPrototypeOf(target) {
      //   return {};
      // },

      // 拦截 `Object.isExtensible(proxy)`，返回一个布尔值
      // isExtensible(target) {
      //   return true;
      // },

      // 拦截 `Object.setPrototypeOf(proxy, proto)`，返回一个布尔值
      // setPrototypeOf(target, proto) {
      //   return true;
      // },

      // 拦截 Proxy 实例作为函数调用的操作
      // apply(target, thisArg, args) {
      //   return target.apply(thisArg, args);
      // },

      // 拦截 Proxy 实例作为构造函数调用的操作
      // construct(target, args) {
      //   return {};
      // }
    };

    const proxy = new Proxy(raw, handler);
    // 取消代理 : Proxy.revocable(target, handler);

    return proxy;
  }

  // Model-View-ViewModel 响应式系统
  function mvvm(state: State, el: HTMLElement): State {
    const reactiveState = reactiveVue3(state);
    compile(reactiveState, el);
    return reactiveState;
  }

  // 解析器
  function compile(state: State, el: HTMLElement) {
    el.childNodes.forEach((node) => {
      switch (node.nodeType) {
        case 1: // Element Node 元素节点
          const elementNode = node as HTMLElement;
          [...elementNode.attributes].forEach((attr) => {
            const attrName = attr.name;
            const attrValue = attr.value;
            if (attrName === "v-model") {
              // 数据双向绑定
              if (isInputRadio(elementNode)) {
                // radio 使用 checked 属性 和 change 事件
                const finalNode = elementNode as HTMLInputElement;
                // 数据 驱动 视图 更新
                watchEffect(() => {
                  finalNode.checked =
                    getNestedValue(state, attrValue) == finalNode.value;
                });
                // 通过事件监听，视图 驱动 数据 更新
                finalNode.addEventListener("change", (e) => {
                  setNestedValue(
                    state,
                    attrValue,
                    (e.target as typeof finalNode).value
                  );
                });
              } else if (isInputCheckbox(elementNode)) {
                // checkbox 使用 checked 属性 和 change 事件
                const finalNode = elementNode as HTMLInputElement;
                // 数据 驱动 视图 更新
                watchEffect(() => {
                  finalNode.checked = getNestedValue(state, attrValue).includes(
                    finalNode.value
                  );
                });
                // 通过事件监听，视图 驱动 数据 更新
                finalNode.addEventListener("change", (e) => {
                  const target = e.target as typeof finalNode;
                  const value = target.value;
                  if (target.checked) {
                    appendUniqueArray(getNestedValue(state, attrValue), value);
                  } else {
                    deleteArrayItem(getNestedValue(state, attrValue), value);
                  }
                });
              } else if (isSelect(elementNode)) {
                // select 使用 value 属性 和 change 事件
                const finalNode = elementNode as HTMLSelectElement;
                // 数据 驱动 视图 更新
                watchEffect(() => {
                  finalNode.value = getNestedValue(state, attrValue);
                });
                // 通过事件监听，视图 驱动 数据 更新
                finalNode.addEventListener("change", (e) => {
                  setNestedValue(
                    state,
                    attrValue,
                    (e.target as typeof finalNode).value
                  );
                });
              } else {
                // input text number ...  和 textarea 使用 value 属性 和 input 事件
                const finalNode = elementNode as
                  | HTMLInputElement
                  | HTMLTextAreaElement;
                // 数据 驱动 视图 更新
                watchEffect(() => {
                  finalNode.value = getNestedValue(state, attrValue);
                });
                // 通过事件监听，视图 驱动 数据 更新
                const cb = (e: Event) => {
                  setNestedValue(
                    state,
                    attrValue,
                    (e.target as typeof finalNode).value
                  );
                };
                finalNode.addEventListener("input", cb);
                // finalNode.addEventListener("change", cb);
              }
            } else if (attrName === "v-text") {
              // 数据 驱动 视图 更新
              watchEffect(() => {
                elementNode.innerText = getNestedValue(state, attrValue);
              });
            } else if (attrName === "v-html") {
              // 数据 驱动 视图 更新
              watchEffect(() => {
                elementNode.innerHTML = getNestedValue(state, attrValue);
              });
            }
          });
          // 递归子节点
          if (elementNode.childNodes.length > 0) {
            compile(state, elementNode);
          }
          break;
        case 3: // Text Node 文本节点
          const textContent = node.textContent || "";
          const reg = /\{\{\s*([^{}\s]+)\s*\}\}/g;
          const matches = reg.exec(textContent);
          if (matches && matches[1]) {
            // 数据 驱动 视图 更新
            watchEffect(() => {
              node.textContent = textContent.replace(
                reg,
                getNestedValue(state, matches[1])
              );
            });
          }
          break;
      }
    });
  }

  return { watchEffect, reactiveVue2, ref, reactiveVue3, reactive, mvvm };
}

export const miniVue = createMiniVue();
